import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import { createCardPayment } from "@api";
import { retry } from "@utils";
import { ConfigurationContext } from "../utils/ConfigurationProvider.jsx";
import { useNavigate } from "react-router-dom";

// This componet is formed of two components because stripe requires the elements context.
const StripePayment = ({
  stripeAccountId,
  paymentParameters,
  onSuccess,
  returnUrl,
  externalSubmitRef,
  onError,
  setLoading,
  onAmountChange,
}) => {
  const [stripeOptions, setStripeOptions] = useState(null);
  const [stripe, setStripe] = useState(null);
  const { config } = useContext(ConfigurationContext);
  const checkoutFormRef = useRef(null);

  useEffect(() => {
    async function setUpStripe() {
      const stripeObject = await loadStripe(config.stripePublicApiKey, {
        betas: ["blocked_card_brands_beta_2"],
      });
      const options = {
        mode: "payment",
        amount: Math.round(paymentParameters.amount * 100),
        currency: "gbp",
        onBehalfOf: stripeAccountId,
        captureMethod: "manual",
        disallowedCardBrands: paymentParameters.amexPaymentAllowed
          ? []
          : ["american_express"],
      };

      setStripeOptions(options);
      setStripe(stripeObject);
    }

    setUpStripe();
  }, [config.stripePublicApiKey, stripeAccountId, paymentParameters]);

  // Set loading to false when Stripe is ready and call onAmountChange
  useEffect(() => {
    if (stripe && stripeOptions) {
      setLoading?.(false);

      // Call onAmountChange with the payment amount (Stripe doesn't have fees like Adyen)
      onAmountChange?.({
        baseAmount: paymentParameters.amount,
        totalAmount: paymentParameters.amount,
        totalFeesAmount: 0,
        totalFeesPercentage: 0,
      });
    }
  }, [
    stripe,
    stripeOptions,
    setLoading,
    onAmountChange,
    paymentParameters.amount,
  ]);

  // Expose `submitPayment` to the parent component
  useEffect(() => {
    if (externalSubmitRef) {
      externalSubmitRef.current = {
        submitPayment: () => checkoutFormRef.current?.submitPayment(),
      };
    }
  }, [externalSubmitRef]);

  return (
    stripe &&
    stripeOptions && (
      <Elements stripe={stripe} options={stripeOptions}>
        <CheckoutForm
          ref={checkoutFormRef}
          paymentParameters={paymentParameters}
          onSuccess={onSuccess}
          returnUrl={returnUrl}
          onError={onError}
        />
      </Elements>
    )
  );
};

const CheckoutForm = forwardRef(
  ({ paymentParameters, onSuccess, returnUrl, onError }, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState();

    const submitPayment = async () => {
      setErrorMessage("");

      if (!stripe) return;

      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError?.();
        return;
      }

      let payment;
      try {
        payment = await retry(
          () =>
            createCardPayment({
              ...paymentParameters,
              paymentProvider: "Stripe",
            }),
          { attempts: 3 }
        );
      } catch {
        setErrorMessage("Network error – please try again.");
        onError?.();
        return;
      }

      if (payment?.tokenExpired) {
        onError?.({ tokenExpired: true });
        return;
      }

      const separator = returnUrl.includes("?") ? "&" : "?";
      const returnUrlWithParams = `${returnUrl}${separator}t=card&p-id=${payment.id}`;

      try {
        const { error } = await stripe.confirmPayment({
          elements,
          clientSecret: payment.clientSecret,
          redirect: paymentParameters.preventRedirect
            ? "if_required"
            : "always",
          confirmParams: {
            return_url: window.location.origin + returnUrlWithParams,
          },
        });

        if (error) {
          const stripeHandlesErrorDisplay =
            error.type === "validation_error" ||
            ["card_declined", "expired_card", "incorrect_cvc"].includes(
              error.code ?? ""
            );

          if (!stripeHandlesErrorDisplay) setErrorMessage(error.message);
          onError?.();
          return;
        }

        onSuccess?.();
        navigate(returnUrlWithParams);
      } catch {
        setErrorMessage("Network error – please try again.");
        onError?.();
      }
    };

    useImperativeHandle(ref, () => ({ submitPayment }));

    return (
      <>
        <form className="w-full mt-2">
          <PaymentElement />
          {errorMessage && (
            <div
              className="text-[#df1b41] text-sm rounded-3 p-2 mt-2"
              role="alert"
            >
              {errorMessage}
            </div>
          )}
        </form>
      </>
    );
  }
);

export { StripePayment };
